import { action, computed, makeObservable, observable, reaction } from "mobx";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { BaseStore } from "../../../../stores/BaseStore";
import { AuthStore } from "stores/AuthStore";
import { MontageService } from "services/MontageService";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { dateTimeColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import { GridRowParams } from "@mui/x-data-grid-pro";
import { DatePickerComponentStore } from "stores/ComponentStores/DatePickerComponentStore";
import { SelectedCardRequestPayload } from "services/SignalsService";
import { v4 as uuidv4 } from "uuid";
import {
    ADDITIONAL_CHARTS_DASHBOARD,
    ISignalsChartDefinition,
    ISignalsMontageCard,
    ISignalsSmallCard,
    SignalsEndpoints,
    SignalsObjectType,
    SMALL_CARDS_DASHBOARD,
} from "../../SignalsModels";
import SignalsPageStore from "../../Store/SignalsPageStore";
import { Props as TrendingProps } from "../../BaseObjects/SignalsTrendingIndicator";
import OrgOptionsStore from "stores/OrgOptionsStore";

export const REPORTS_ITEM_GROUP = "Charts";
export const TEXT_ITEM_GROUP = "Custom Text";
export const MONTAGE_ITEM_GROUP = "Montages";
export const INSIGHT_ITEM_GROUP = "Small Cards";
/** Reports the viewer sees as Deprecated. Membership is derived from status, not declared. */
export const LEGACY_ITEM_GROUP = "Legacy Reports";

export interface InsightType {
    title: string;
    presentationTitle?: string;
    dataEndpoint: string;
    dummyInfo: {
        stat: string;
        secondaryText?: string;
        trendingProps: TrendingProps;
    };
    options?: string;
}

export const insightTypeMap: Record<string, InsightType> = {
    TotalConversations: {
        dummyInfo: {
            stat: "164",
            trendingProps: { inputValue: 12.3, isGrayOnly: true },
        },
        presentationTitle: "",
        title: "Total Conversations",
        dataEndpoint: "TotalConversations",
    },
    AvgCallDuration: {
        dummyInfo: { stat: "9m 23s", trendingProps: { inputValue: -12.3 } },
        presentationTitle: "Avg Call Duration",
        title: "Avg Call Duration",
        dataEndpoint: "AvgCallDuration",
    },
    ConversationsWithAnEddy: {
        dummyInfo: { stat: "8.30%", trendingProps: { inputValue: 12.3 } },
        presentationTitle: "",
        title: "Conversations with an Eddy",
        dataEndpoint: "ConversationsWithAnEddy",
    },
    AvgModuleScore: {
        dummyInfo: {
            stat: "75%",
            trendingProps: { inputValue: 12.3, isNegativeRed: true },
        },
        presentationTitle: "",
        title: "Avg Module Score",
        dataEndpoint: SignalsEndpoints.smallCards.AvgModuleScoreId,
    },
    ShareOfConversations: {
        dummyInfo: {
            stat: "42.10%",
            secondaryText: "80 out of 200",
            trendingProps: { inputValue: 12.3, isGrayOnly: true },
        },
        presentationTitle: "",
        title: "Share of Conversations",
        dataEndpoint: SignalsEndpoints.smallCards.ShareOfConversationsId,
    },
};

export const LoadingAllMontages = "GET MONTAGES";

export type SelectedCard = {
    id?: string;
    type: SignalsObjectType;
    info: ISignalsChartDefinition | string | number;
    order?: number;
    label?: string;
};

export type SelectedInsight = {
    id?: string;
    insight: string;
    order?: number;
};

@AcxStore
class SignalsCardSelectorStore extends BaseStore {
    readonly authStore: AuthStore;
    readonly montageDgStore: AcxDataGridStore;
    readonly montageService: MontageService;

    public readonly datePickerStore = new DatePickerComponentStore();
    readonly signalsPageStore: SignalsPageStore;
    readonly orgOptionsStore: OrgOptionsStore;
    readonly rootStore: IRootStore;

    @observable
    showReportSelector: boolean = false;

    @observable
    selectedItemGroup: string = REPORTS_ITEM_GROUP;

    @observable
    selectedCards: SelectedCard[] = [];

    // Handled separately from selectedCards for UI grouping, but still ordered
    // alongside them when building the add-cards payload.
    @observable
    selectedInsights: SelectedInsight[] = [];

    @observable
    savePDFName: string = "";

    /**
     * Resets the dialog to its initial state. Keep the logic centralized so the dialog and header
     * can both use the same cleanup.
     */
    @action
    resetSelections() {
        this.showReportSelector = false;
        this.selectedItemGroup = REPORTS_ITEM_GROUP;
        this.montageDgStore.removeAllSelectedRows();
        this.selectedCards = [];
        this.selectedInsights = [];
        this.savePDFName = "";
    }

    public constructor(rootStore: IRootStore) {
        super("Signals Store");
        makeObservable(this);

        this.authStore = rootStore.getStore(AuthStore);

        this.signalsPageStore = rootStore.getStore(SignalsPageStore);
        this.orgOptionsStore = rootStore.getStore(OrgOptionsStore);

        this.montageService = new MontageService();
        this.montageDgStore = new AcxDataGridStore(
            "Add_Card_Montages",
            "Signals",
        );
        this.montageDgStore.checkboxSelection = true;
        this.montageDgStore.removeHeight = "64px";

        this.rootStore = rootStore;

        const columns: IColDef[] = [
            {
                headerName: "Id",
                hide: true,
                field: "id",
            },
            {
                headerName: "Name",
                field: "name",
                flex: 1,
            },
            {
                headerName: "Owner",
                field: "createdBy",
                flex: 1,
            },
            {
                headerName: "Created Date",
                field: "createdOn",
                flex: 1,
                ...dateTimeColumnType,
            },
            {
                headerName: "File Path",
                field: "fileName",
                flex: 1,
            },
        ];

        this.montageDgStore.setColumns(columns);

        // disable checkbox if montage already exists in current dashboard
        this.montageDgStore.isRowSelectable = (param: GridRowParams) => {
            const currentMontageIds = (
                this.signalsPageStore.currentDashboard?.signalsObjects.filter(
                    (obj) => obj.objectType === SignalsObjectType.MontageCard,
                ) as ISignalsMontageCard[]
            ).map((mont) => mont.montageId);

            return !currentMontageIds.includes(param.row.id.toString());
        };

        this.montageDgStore.onChange = async () => {
            const selectedRowIds = this.montageDgStore.selectedRowIds;
            selectedRowIds.forEach((rowId) => {
                // If the montage is not already selected, add it to the selectedCards
                if (!this.selectedCards.some((card) => card.info === rowId)) {
                    this.addSelectedCard({
                        type: SignalsObjectType.MontageCard,
                        info: rowId,
                    });
                }
            });
            // Remove any selected montages that are not in the current selection
            this.setSelectedCards(
                this.selectedCards.filter(
                    (card) =>
                        card.type !== SignalsObjectType.MontageCard ||
                        selectedRowIds.includes(card.info as number),
                ),
            );
        };

        reaction(
            () => [
                this.showReportSelector,
                this.datePickerStore.beginDate,
                this.datePickerStore.endDate,
            ],
            () => {
                if (this.showReportSelector) {
                    this.getAllMontages();
                }
            },
            { fireImmediately: true },
        );
    }

    @action
    getAllMontages = async () => {
        this.setupAsyncTask(LoadingAllMontages, async () => {
            const res = await this.montageService.listMontages(
                this.datePickerStore.beginDate,
                this.datePickerStore.endDate,
            );
            this.montageDgStore.rows = res;
            return res;
        });
    };

    @action
    setSelectedInsights = (val: SelectedInsight[]) => {
        this.selectedInsights = val;
    };

    @action
    addSelectedInsight = (insight: SelectedInsight) => {
        this.selectedInsights = [
            ...this.selectedInsights,
            {
                ...insight,
                id: insight.id ?? uuidv4(),
                order: insight.order ?? this.getNextSelectionOrder(),
            },
        ];
    };

    @action
    removeSelectedInsight = (insight: string) => {
        const idx = this.findLastMatchingIndex(
            this.selectedInsights,
            (item) => item.insight === insight,
        );
        if (idx === -1) return;
        const next = [...this.selectedInsights];
        next.splice(idx, 1);
        this.selectedInsights = next;
    };

    @action
    removeAllSelectedInsights = (insight: string) => {
        this.selectedInsights = this.selectedInsights.filter(
            (item) => item.insight !== insight,
        );
    };

    getInsightCount = (insight: string) =>
        this.selectedInsights.filter((item) => item.insight === insight).length;

    @action
    addSelectedCard = (card: SelectedCard) => {
        this.selectedCards = [
            ...this.selectedCards,
            {
                ...card,
                id: card.id ?? uuidv4(),
                order: card.order ?? this.getNextSelectionOrder(),
            },
        ];
    };

    private getNextSelectionOrder = () => {
        const currentMaxOrder = Math.max(
            0,
            ...this.selectedCards.map((card) => card.order ?? 0),
            ...this.selectedInsights.map((insight) => insight.order ?? 0),
        );

        return currentMaxOrder + 1;
    };

    private findLastMatchingIndex = <T,>(
        items: T[],
        predicate: (item: T) => boolean,
    ) => {
        for (let i = items.length - 1; i >= 0; i -= 1) {
            if (predicate(items[i])) {
                return i;
            }
        }

        return -1;
    };

    @action
    removeSelectedCardById = (cardId: string) => {
        this.selectedCards = this.selectedCards.filter(
            (card) => card.id !== cardId,
        );
    };

    @action
    setSelectedCards = (cards: SelectedCard[]) => {
        this.selectedCards = cards;
    };

    @computed
    get totalCardCount() {
        return (
            this.selectedCards.length +
            (this.signalsPageStore.currentDashboard?.signalsObjects.length || 0)
        );
    }

    @computed
    get defaultInsightTypes() {
        return (
            this.signalsPageStore.allDashboards
                .values()
                .find((dash) => dash.title === SMALL_CARDS_DASHBOARD)
                ?.signalsObjects || []
        );
    }

    @computed
    get selectedCharts() {
        return this.selectedCards.filter(
            (card) => card.type === SignalsObjectType.Chart,
        );
    }

    @computed
    get selectedTableCards() {
        return this.selectedCards.filter(
            (card) => card.type === SignalsObjectType.Table,
        );
    }

    @computed
    get selectedUniqueCards() {
        return this.selectedCards.filter(
            (card) => card.type === SignalsObjectType.Unique,
        );
    }

    @computed
    get selectedReportCards() {
        return [
            ...this.selectedCharts,
            ...this.selectedTableCards,
            ...this.selectedUniqueCards,
        ];
    }

    getChartCount = (chartId: string) =>
        this.selectedCharts.filter(
            (card) => (card.info as ISignalsChartDefinition).id === chartId,
        ).length;

    getTableCount = (chartId: string) =>
        this.selectedTableCards.filter(
            (card) => (card.info as ISignalsChartDefinition).id === chartId,
        ).length;

    getUniqueChartCount = (chartId: string) =>
        this.selectedUniqueCards.filter(
            (card) => (card.info as ISignalsChartDefinition).id === chartId,
        ).length;

    private isExtractedConversationDataChart = (
        chart: ISignalsChartDefinition,
    ) => {
        const endpointPath = chart.dataEndpoint?.split("?")[0];
        return (
            endpointPath ===
            SignalsEndpoints.additionalReports.ExtractedConversationDataId
        );
    };

    private hasExtractedConversationDataOptions = () => {
        const metadataClassifierIds = new Set(
            this.orgOptionsStore.extendedMetadataObjectClassifiers.map(
                (classifier) => classifier.id,
            ),
        );

        return this.orgOptionsStore.extractionLlmClassifiers.some(
            (classifier) =>
                classifier.potentialExtendedMetadataClassifierIds.some((id) =>
                    metadataClassifierIds.has(id),
                ),
        );
    };

    getChartUnavailableReason = (chart: ISignalsChartDefinition) => {
        if (!this.isExtractedConversationDataChart(chart)) {
            return undefined;
        }

        if (
            this.orgOptionsStore.extractionLlmClassifiersLoading ||
            this.orgOptionsStore.extendedMetadataObjectClassifiersLoading
        ) {
            return "Checking available extraction models...";
        }

        if (!this.hasExtractedConversationDataOptions()) {
            return "You do not have access to any extraction models available for this report.";
        }

        return undefined;
    };

    @action
    addChartSelection = (chart: ISignalsChartDefinition) => {
        this.addSelectedCard({ type: SignalsObjectType.Chart, info: chart });
    };

    @action
    addTableSelection = (chart: ISignalsChartDefinition) => {
        if (this.getChartUnavailableReason(chart)) {
            return;
        }

        this.addSelectedCard({ type: SignalsObjectType.Table, info: chart });
    };

    @action
    addUniqueChartSelection = (chart: ISignalsChartDefinition) => {
        this.addSelectedCard({ type: SignalsObjectType.Unique, info: chart });
    };

    @action
    removeChartSelection = (chartId: string) => {
        const idx = this.findLastMatchingIndex(
            this.selectedCards,
            (card) =>
                card.type === SignalsObjectType.Chart &&
                (card.info as ISignalsChartDefinition).id === chartId,
        );
        if (idx === -1) return;
        this.selectedCards = [
            ...this.selectedCards.slice(0, idx),
            ...this.selectedCards.slice(idx + 1),
        ];
    };

    @action
    removeTableSelection = (chartId: string) => {
        const idx = this.findLastMatchingIndex(
            this.selectedCards,
            (card) =>
                card.type === SignalsObjectType.Table &&
                (card.info as ISignalsChartDefinition).id === chartId,
        );
        if (idx === -1) return;
        this.selectedCards = [
            ...this.selectedCards.slice(0, idx),
            ...this.selectedCards.slice(idx + 1),
        ];
    };

    @action
    removeUniqueChartSelection = (chartId: string) => {
        const idx = this.findLastMatchingIndex(
            this.selectedCards,
            (card) =>
                card.type === SignalsObjectType.Unique &&
                (card.info as ISignalsChartDefinition).id === chartId,
        );
        if (idx === -1) return;
        this.selectedCards = [
            ...this.selectedCards.slice(0, idx),
            ...this.selectedCards.slice(idx + 1),
        ];
    };

    @action
    removeAllChartSelections = (chartId: string) => {
        this.selectedCards = this.selectedCards.filter(
            (card) =>
                !(
                    card.type === SignalsObjectType.Chart &&
                    (card.info as ISignalsChartDefinition).id === chartId
                ),
        );
    };

    @action
    removeAllTableSelections = (chartId: string) => {
        this.selectedCards = this.selectedCards.filter(
            (card) =>
                !(
                    card.type === SignalsObjectType.Table &&
                    (card.info as ISignalsChartDefinition).id === chartId
                ),
        );
    };

    @action
    removeAllUniqueChartSelections = (chartId: string) => {
        this.selectedCards = this.selectedCards.filter(
            (card) =>
                !(
                    card.type === SignalsObjectType.Unique &&
                    (card.info as ISignalsChartDefinition).id === chartId
                ),
        );
    };

    @computed
    get selectedTextCards() {
        return this.selectedCards.filter(
            (card) => card.type === SignalsObjectType.TextCard,
        );
    }

    @computed
    get selectedMontages() {
        return this.selectedCards.filter(
            (card) => card.type === SignalsObjectType.MontageCard,
        );
    }

    /**
     * Reports pulled from the standard dashboards (same source as Signals v1).
     * Duplicates can be added to the same dashboard.
     * **Table and Unique Cards are included in this list** since they are considered reports for adding
     */
    @computed
    get availableCharts(): ISignalsChartDefinition[] {
        let charts: ISignalsChartDefinition[] = [];
        this.signalsPageStore.standardDashboards.forEach((dash) => {
            dash.signalsObjects
                .filter(
                    (obj) =>
                        obj.objectType === SignalsObjectType.Chart ||
                        obj.objectType === SignalsObjectType.Table ||
                        obj.objectType === SignalsObjectType.Unique,
                )
                .forEach((chart) =>
                    charts.push(chart as ISignalsChartDefinition),
                );
        });
        // Filter out the auto-generated published model results charts since there are too many of
        // them — including the published grouped variant (one per published sub-group), which lives on
        // its generated "{parent} Results" dashboard rather than the add-card palette. The interactive
        // "GroupedModelResults" template has a different endpoint and stays addable.
        charts = charts.filter(
            (chart) =>
                !chart.dataEndpoint?.includes(
                    SignalsEndpoints.additionalReports
                        .PublishedModelResultsCountId,
                ) &&
                !chart.dataEndpoint?.includes(
                    SignalsEndpoints.additionalReports
                        .PublishedModelResultsOverTimeId,
                ) &&
                !chart.dataEndpoint?.includes(
                    SignalsEndpoints.additionalReports
                        .PublishedGroupedModelResultsId,
                ),
        );

        // Hide module-score charts when the user lacks the Module Scores permission
        if (!this.authStore.canUserView("Module Scores")) {
            const moduleScoreEndpoints: string[] = [
                SignalsEndpoints.trends.AverageScoreByModuleOverTimeID,
                SignalsEndpoints.trends.AverageScoreByQuestionOverTimeID,
                SignalsEndpoints.trends.QualityScoreOverTimeID,
                SignalsEndpoints.trends.AverageScoreBySkillID,
                SignalsEndpoints.eddyReports.AverageScoreByQuestionID,
                SignalsEndpoints.eddyReports
                    .AverageScoreByQuestionAndEddyOverTimeID,
            ];
            charts = charts.filter(
                (chart) =>
                    !chart.dataEndpoint ||
                    !moduleScoreEndpoints.includes(chart.dataEndpoint),
            );
        }

        this.signalsPageStore.allDashboards
            .values()
            .find((dash) => dash.title === ADDITIONAL_CHARTS_DASHBOARD)
            ?.signalsObjects.filter(
                (obj) =>
                    obj.objectType === SignalsObjectType.Chart ||
                    obj.objectType === SignalsObjectType.Table,
            )
            .forEach((chart) => charts.push(chart as ISignalsChartDefinition));

        const uniqueById = new Map<string, ISignalsChartDefinition>();
        charts.forEach((chart) => {
            if (!uniqueById.has(chart.id)) {
                uniqueById.set(chart.id, chart);
            }
        });

        return Array.from(uniqueById.values()).sort((a, b) =>
            (a.title || "").localeCompare(b.title || ""),
        );
    }

    @computed
    get insightTypes(): InsightType[] {
        return this.defaultInsightTypes.map((obj) => {
            const smallCard = obj as ISignalsSmallCard;
            const dataEndpoint = smallCard.dataEndpoint || "";
            const presentationTitle =
                insightTypeMap[dataEndpoint]?.presentationTitle;
            const dummyInfo = insightTypeMap[dataEndpoint]?.dummyInfo ?? {
                stat: "--",
                trendingProps: { inputValue: 0 },
            };
            return {
                title: smallCard.title,
                dataEndpoint,
                dummyInfo,
                ...(presentationTitle && {
                    presentationTitle,
                }),
                options: smallCard.availableOptionObjects,
            };
        });
    }

    @action
    addTextCard = (text: string = "") => {
        this.addSelectedCard({
            type: SignalsObjectType.TextCard,
            info: text,
        });
    };

    @action
    updateTextCard = (cardId: string, text: string) => {
        this.selectedCards = this.selectedCards.map((card) =>
            card.id === cardId ? { ...card, info: text } : card,
        );
    };

    @action
    removeTextCard = (cardId: string) => {
        this.selectedCards = this.selectedCards.filter(
            (card) => card.id !== cardId,
        );
    };

    public buildSelectedCardsPayload = (): SelectedCardRequestPayload[] => {
        const selectedSmallCards = this.selectedInsights.map(
            (insight, index) => {
                const definition = this.insightTypes.find(
                    (type) => type.dataEndpoint === insight.insight,
                );

                return {
                    type: SignalsObjectType.SmallCard,
                    info: {
                        title:
                            definition?.presentationTitle ??
                            definition?.title ??
                            insight.insight,
                        dataEndpoint:
                            definition?.dataEndpoint ?? insight.insight,
                        availableOptionObjects: definition?.options,
                    },
                    order: insight.order,
                    selectionIndex: index,
                };
            },
        );

        const selectedCards = this.selectedCards.map((card, index) => {
            let info: any = card.info;

            if (
                card.type === SignalsObjectType.Chart ||
                card.type === SignalsObjectType.Table ||
                card.type === SignalsObjectType.Unique
            ) {
                const chart = card.info as ISignalsChartDefinition;
                info = {
                    dataEndpoint: chart.dataEndpoint,
                    title: chart.title,
                    subtitle: chart.subtitle,
                    xTitle: chart.xTitle,
                    yTitle: chart.yTitle,
                    vizOptions: chart.vizOptions,
                    typeOfChart: chart.typeOfChart,
                    typeOfDataSelectedInChart: chart.typeOfDataSelectedInChart,
                    availableRenderModes: chart.availableRenderModes,
                    defaultRenderMode: chart.defaultRenderMode,
                    isPaginated: chart.isPaginated,
                    compareToPreviousModeIsAvailable:
                        chart.compareToPreviousModeIsAvailable,
                    isTemporal: chart.isTemporal,
                    showDataGrid: chart.showDataGrid,
                    showSpotlight: chart.showSpotlight,
                    showAnalyzeWithKBo: chart.showAnalyzeWithKBo,
                    objectFilters: chart.objectFilters,
                    orderRuleId: chart.orderRuleId,
                };
            }

            return {
                id: card.id,
                type: card.type,
                info,
                order: card.order,
                label: card.label,
                selectionIndex: this.selectedInsights.length + index,
            };
        });

        return (
            [...selectedSmallCards, ...selectedCards]
                .sort((a, b) => {
                    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
                    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;

                    if (orderA !== orderB) {
                        return orderA - orderB;
                    }

                    return a.selectionIndex - b.selectionIndex;
                })
                // The server places cards in the order sent, so selection order
                // only decides the sequence and is not sent itself.
                .map(({ selectionIndex, order, ...card }) => card)
        );
    };

    @action
    clearTextCards = () => {
        this.selectedCards = this.selectedCards.filter(
            (card) => card.type !== SignalsObjectType.TextCard,
        );
    };

    @action
    setTextCardContent = (text: string) => {
        if (!text) {
            this.clearTextCards();
            return;
        }

        const firstTextCard = this.selectedTextCards[0];

        if (firstTextCard?.id) {
            this.updateTextCard(firstTextCard.id, text);
        } else {
            this.addTextCard(text);
        }
    };
}

export default SignalsCardSelectorStore;
